// Mock data for Niloman HX Smart RO Website

export const heroData = {
  title: "Smart IoT-Enabled ROs for Homes & Businesses",
  subtitle: "Experience the future of water purification with real-time monitoring, app control, and reliable purification technology.",
  ctaPrimary: "Book a Free Demo",
  ctaSecondary: "Talk to an Expert",
  heroImage: "https://customer-assets.emergentagent.com/job_smart-ro-monitor/artifacts/oa32ccpd_ro-image-family.png"
};

export const features = [
  {
    id: 1,
    title: "Real-time TDS Monitoring",
    description: "Track water quality instantly with live TDS sensors and get alerts on water purity levels.",
    icon: "Activity"
  },
  {
    id: 2,
    title: "Filter Health Alerts",
    description: "Receive predictive maintenance alerts and never worry about filter replacement timing.",
    icon: "BellRing"
  },
  {
    id: 3,
    title: "Mobile App Control",
    description: "Control your RO system remotely and access detailed usage analytics from anywhere.",
    icon: "Smartphone"
  },
  {
    id: 4,
    title: "Cost Efficiency",
    description: "Lower lifetime costs through smart scheduling, leak detection, and optimized water usage.",
    icon: "TrendingDown"
  }
];

export const productCategories = [
  {
    id: 1,
    name: "Home Smart RO",
    description: "Perfect for modern homes with advanced IoT monitoring",
    image: "https://customer-assets.emergentagent.com/job_smart-ro-monitor/artifacts/oa32ccpd_ro-image-family.png",
    category: "Home"
  },
  {
    id: 2,
    name: "Commercial Smart RO",
    description: "High-capacity purification for offices, gyms, and academies",
    image: "https://customer-assets.emergentagent.com/job_smart-ro-monitor/artifacts/6osugwbn_ro%20cabinet.jpeg",
    category: "Commercial"
  },
  {
    id: 3,
    name: "IoT Retrofit Kit",
    description: "Upgrade your existing purifier with smart IoT capabilities",
    image: "https://images.unsplash.com/photo-1667984550719-1c44d1d07ed7",
    category: "Retrofit"
  }
];

export const products = [
  {
    id: 1,
    name: "Niloman HX Home Smart RO",
    category: "Home",
    capacity: "10 Liters",
    stages: "7-Stage Purification (RO + UV + UF + TDS Control)",
    idealFor: "Families of 4-6 members",
    features: [
      "Real-time TDS monitoring - Track input and output water quality instantly",
      "Predictive maintenance alerts - Pump and electronics health monitoring",
      "Smart filter tracking - Never miss a filter change with usage-based alerts",
      "Alkaline pH balance (7.5-9.5) - Perfect pH for optimal health",
      "Enhanced ORP for antioxidant benefits - Powerful antioxidant-rich water",
      "Essential mineral enrichment - Copper, calcium, magnesium added back",
      "Interactive onboard display - See exactly what you're drinking"
    ],
    price: "₹18,999",
    image: "https://customer-assets.emergentagent.com/job_smart-ro-monitor/artifacts/oa32ccpd_ro-image-family.png"
  },
  {
    id: 2,
    name: "Niloman HX Commercial RO",
    category: "Commercial",
    capacity: "25 Liters",
    stages: "8-Stage Purification (RO + UV + UF + Alkaline + TDS Control)",
    idealFor: "Offices, gyms, academies (50-100 people)",
    features: [
      "High-capacity purification (75L per hour)",
      "Multi-sensor array for comprehensive monitoring",
      "API integration for facility management",
      "Predictive maintenance algorithms",
      "Web dashboard + mobile app access",
      "4G/Wi-Fi dual connectivity",
      "Automated service scheduling"
    ],
    price: "₹45,999",
    image: "https://customer-assets.emergentagent.com/job_smart-ro-monitor/artifacts/6osugwbn_ro%20cabinet.jpeg"
  },
  {
    id: 3,
    name: "Niloman HX IoT Retrofit Kit",
    category: "Retrofit",
    capacity: "Compatible with most RO systems",
    stages: "IoT Upgrade (Works with existing purification)",
    idealFor: "Existing RO purifier owners",
    features: [
      "Easy installation on existing RO systems",
      "TDS monitoring sensors",
      "Flow and usage tracking",
      "Mobile app connectivity",
      "Real-time alerts and notifications",
      "Cloud data storage",
      "6-month battery backup"
    ],
    price: "₹7,999",
    image: "https://images.unsplash.com/photo-1667984550719-1c44d1d07ed7"
  }
];

export const technologySteps = [
  {
    id: 1,
    title: "Sensor Network",
    description: "Advanced sensors in your RO system continuously track TDS levels, water flow, tank capacity, filter life, and potential leaks.",
    icon: "Cpu",
    image: "https://images.unsplash.com/photo-1667984550708-a6beba23cb4c"
  },
  {
    id: 2,
    title: "Secure Data Transmission",
    description: "Data is encrypted and sent securely via Wi-Fi or 4G to our cloud platform, ensuring complete privacy and reliability.",
    icon: "Shield",
    image: "https://images.unsplash.com/photo-1492962827063-e5ea0d8c01f5"
  },
  {
    id: 3,
    title: "Real-time Dashboard",
    description: "Access comprehensive water quality data, usage analytics, and system health through our intuitive mobile and web applications.",
    icon: "BarChart3",
    image: "https://images.unsplash.com/photo-1640694514279-090bb1b09ee9"
  },
  {
    id: 4,
    title: "Predictive Maintenance",
    description: "Our AI analyzes patterns to predict maintenance needs. Service teams receive alerts automatically and can schedule visits proactively.",
    icon: "Wrench",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71"
  }
];

export const useCases = [
  {
    id: 1,
    title: "Homes & Apartments",
    description: "Ensure your family drinks only the purest water with 24/7 quality monitoring and instant alerts.",
    benefits: [
      "Peace of mind with real-time quality tracking",
      "Reduced service costs with predictive maintenance",
      "Mobile alerts for filter changes",
      "Track daily water consumption"
    ],
    icon: "Home",
    image: "https://images.unsplash.com/photo-1600679472233-eabc13b79f07"
  },
  {
    id: 2,
    title: "Offices & Coworking Spaces",
    description: "Provide employees with safe drinking water while maintaining compliance and reducing operational costs.",
    benefits: [
      "Centralized monitoring across multiple dispensers",
      "Automated compliance logging",
      "Cost tracking and usage analytics",
      "Reduced downtime with proactive maintenance"
    ],
    icon: "Building2",
    image: "https://images.unsplash.com/photo-1544198841-10f34f31f8dd"
  },
  {
    id: 3,
    title: "Gyms, Sports Academies & Schools",
    description: "Keep athletes and students hydrated with monitored, high-quality water that meets safety standards.",
    benefits: [
      "High-capacity purification for heavy usage",
      "Health and safety compliance assurance",
      "Real-time quality monitoring",
      "Usage insights for facility planning"
    ],
    icon: "Dumbbell",
    image: "https://images.pexels.com/photos/5383488/pexels-photo-5383488.jpeg"
  },
  {
    id: 4,
    title: "Cafes & F&B Outlets",
    description: "Deliver exceptional beverage quality with purified water while maintaining hygiene standards.",
    benefits: [
      "Consistent water quality for better taste",
      "Hygiene compliance documentation",
      "Automated maintenance alerts",
      "Cost savings on bottled water"
    ],
    icon: "Coffee",
    image: "https://images.unsplash.com/photo-1665893986001-b617592dd717"
  }
];

export const pricingPlans = [
  {
    id: 1,
    name: "Basic Home Plan",
    description: "Essential smart RO for small families",
    price: "₹18,999",
    period: "one-time + ₹1,999/year service",
    features: [
      "Niloman HX Home Smart RO (10L)",
      "Mobile app access",
      "Real-time TDS monitoring",
      "Basic usage analytics",
      "Annual maintenance (2 visits)",
      "1-year warranty",
      "Email support"
    ],
    popular: false
  },
  {
    id: 2,
    name: "Advanced Home Plan",
    description: "Complete smart water solution for families",
    price: "₹24,999",
    period: "one-time + ₹2,999/year service",
    features: [
      "Niloman HX Home Smart RO (10L)",
      "Mobile app + web dashboard",
      "Advanced analytics & insights",
      "Predictive maintenance alerts",
      "Quarterly maintenance (4 visits)",
      "2-year extended warranty",
      "Priority support",
      "Free filter replacement (1st year)"
    ],
    popular: true
  },
  {
    id: 3,
    name: "Commercial Plan",
    description: "Enterprise solution for businesses",
    price: "₹45,999",
    period: "one-time + Custom AMC",
    features: [
      "Niloman HX Commercial RO (25L)",
      "Multi-device dashboard",
      "API access for integration",
      "Comprehensive analytics",
      "Monthly maintenance visits",
      "3-year warranty",
      "Dedicated account manager",
      "24/7 priority support",
      "Custom integrations available"
    ],
    popular: false
  }
];

export const aboutData = {
  story: "Niloman HX was founded by an IoT technology developer with a vision to bring transparency and intelligence to water purification. Frustrated by the lack of monitoring and service reliability in traditional RO systems, we set out to create a smarter solution for Indian homes and businesses.",
  mission: "Our mission is to provide safe, monitored drinking water with complete transparency and real-time data insights. We believe every drop of water you consume should be tracked, verified, and optimized.",
  vision: "We envision a network of connected purifiers across Indian cities, providing comprehensive insights and automated service management to ensure water safety and quality for all.",
  values: [
    {
      title: "Innovation",
      description: "Leveraging cutting-edge IoT technology for water safety",
      icon: "Lightbulb"
    },
    {
      title: "Transparency",
      description: "Complete visibility into your water quality and system health",
      icon: "Eye"
    },
    {
      title: "Reliability",
      description: "Predictive maintenance ensures uninterrupted pure water supply",
      icon: "Shield"
    },
    {
      title: "Sustainability",
      description: "Optimized water usage and reduced waste through smart monitoring",
      icon: "Leaf"
    }
  ]
};

export const trustBadges = [
  {
    id: 1,
    title: "Made in India",
    icon: "Flag"
  },
  {
    id: 2,
    title: "IoT Enabled",
    icon: "Wifi"
  },
  {
    id: 3,
    title: "Food-grade Components",
    icon: "ShieldCheck"
  },
  {
    id: 4,
    title: "ISO Certified",
    icon: "Award"
  }
];

export const contactInfo = {
  email: "info@nilomanhx.com",
  phone: "+91 9217401431",
  whatsapp: "+91 9217401431",
  address: "LGF-3, Omaxe Green valley Plaza, Gurukul Road, Faridabad-121010, Haryana, India"
};

export const formSubmissions = {
  demo: [],
  contact: []
};
